import { useEffect, useState } from "react";
import { Modal, Text, Paper, Flex, Button, List, ThemeIcon, Box, Badge } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconInfoCircle, IconCircleCheck } from "@tabler/icons-react";
import trpc from "@/shared/utils/trpc";
import PaymentMethodModal from "@/pages/interests/components/PaymentMethodModal";

interface ApplyVerificationModalProps {
  opened: boolean;
  onClose: () => void;
  onApply: () => void;
  isLoading?: boolean;
  verificationData?: any;
}

export default function ApplyVerificationModal({
  opened,
  onClose,
  onApply,
  isLoading = false,
  verificationData = null,
}: ApplyVerificationModalProps) {
  const [paymentModalOpened, { open: openPaymentModal, close: closePaymentModal }] = useDisclosure(false);
  const [verificationFeeCredits, setVerificationFeeCredits] = useState(0);

  useEffect(() => {
    if (!opened) return;
    trpc.paymentConfig.get.query().then((config) => {
      setVerificationFeeCredits(config.verificationFeeCredits);
    });
  }, [opened]);

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={
          <Text fw={500} fz="lg" c="slate.8">
            {verificationData ? "System Verification Result" : "Apply for Verification"}
          </Text>
        }
        centered
        size={verificationData ? "xl" : "lg"}
        padding="lg"
      >
        {verificationData ? (
          <Box>
            <Paper p="md" radius="md" mb="lg" bg="green.0" style={{ border: "1px solid var(--mantine-color-green-2)" }}>
              <Flex gap="sm" align="center">
                <ThemeIcon size={24} radius="xl" color="green" variant="light">
                  <IconCircleCheck size={18} />
                </ThemeIcon>
                <Text c="green.7" fw={600} size="sm">
                  System verification completed successfully!
                </Text>
              </Flex>
            </Paper>

            <Paper
              p="lg"
              withBorder
              style={{
                maxHeight: "500px",
                overflowY: "auto",
                backgroundColor: "var(--mantine-color-gray-0)",
              }}
            >
              <Flex direction="column" gap="lg">
                {verificationData.companyName && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      COMPANY NAME
                    </Text>
                    <Text size="sm" fw={500}>
                      {verificationData.companyName}
                    </Text>
                  </Flex>
                )}

                {verificationData.registrationNumber && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      REGISTRATION NUMBER
                    </Text>
                    <Text size="sm">{verificationData.registrationNumber}</Text>
                  </Flex>
                )}

                {verificationData.companyType && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      COMPANY TYPE
                    </Text>
                    <Text size="sm">{verificationData.companyType}</Text>
                  </Flex>
                )}

                {verificationData.companyStatus && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      COMPANY STATUS
                    </Text>
                    <Badge color="green" variant="light" size="md">
                      {verificationData.companyStatus}
                    </Badge>
                  </Flex>
                )}

                {verificationData.dateOfIncorporation && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      DATE OF INCORPORATION
                    </Text>
                    <Text size="sm">{verificationData.dateOfIncorporation}</Text>
                  </Flex>
                )}

                {verificationData.companyAddress && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      COMPANY ADDRESS
                    </Text>
                    <Text size="sm">{verificationData.companyAddress}</Text>
                  </Flex>
                )}

                {verificationData.natureOfBusiness && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      NATURE OF BUSINESS
                    </Text>
                    <Text size="sm">{verificationData.natureOfBusiness}</Text>
                  </Flex>
                )}

                {verificationData.authorizedCapital && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      AUTHORIZED CAPITAL
                    </Text>
                    <Text size="sm">{verificationData.authorizedCapital}</Text>
                  </Flex>
                )}

                {verificationData.paidUpCapital && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      PAID UP CAPITAL
                    </Text>
                    <Text size="sm">{verificationData.paidUpCapital}</Text>
                  </Flex>
                )}

                {verificationData.email && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      EMAIL
                    </Text>
                    <Text size="sm">{verificationData.email}</Text>
                  </Flex>
                )}

                {verificationData.phoneNumber && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      PHONE NUMBER
                    </Text>
                    <Text size="sm">{verificationData.phoneNumber}</Text>
                  </Flex>
                )}

                {/* Additional fields that might be present */}
                {Object.keys(verificationData)
                  .filter(
                    (key) =>
                      ![
                        "companyName",
                        "registrationNumber",
                        "companyType",
                        "companyStatus",
                        "dateOfIncorporation",
                        "companyAddress",
                        "natureOfBusiness",
                        "authorizedCapital",
                        "paidUpCapital",
                        "email",
                        "phoneNumber",
                      ].includes(key) &&
                      verificationData[key] &&
                      typeof verificationData[key] !== "object"
                  )
                  .map((key) => (
                    <Flex direction="column" key={key}>
                      <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                        {key.replace(/([A-Z])/g, " $1").toUpperCase()}
                      </Text>
                      <Text size="sm">{verificationData[key]}</Text>
                    </Flex>
                  ))}
              </Flex>
            </Paper>

            <Flex justify="flex-end" mt="lg">
              <Button onClick={onClose}>Close</Button>
            </Flex>
          </Box>
        ) : (
          <>
            <Text fz="sm" c="slate.5" mb="md">
              Are you sure you want to submit your profile for verification. Once submitted, your details will be
              reviewed, and certain fields will be locked after approval.
            </Text>

            <Paper
              p="md"
              radius="lg"
              mb="xl"
              bg="primary.0"
              style={{ border: "1px solid var(--mantine-color-primary-1)" }}
            >
              <Flex gap="sm" align="flex-start">
                <ThemeIcon size={20} radius="xl" color="primary" variant="transparent">
                  <IconInfoCircle size={18} />
                </ThemeIcon>
                <Flex direction="column">
                  <Text fz="sm" fw={600} c="primary.6" mb="xs">
                    Verification Requirements
                  </Text>
                  <List
                    size="sm"
                    spacing={4}
                    styles={{
                      item: {
                        color: "var(--mantine-color-primary-6)",
                        fontSize: "13px",
                      },
                    }}
                  >
                    <List.Item>
                      Once verified, certain profile details used for authentication cannot be edited.
                    </List.Item>
                    <List.Item>All documents are securely stored and reviewed by our compliance team.</List.Item>
                    <List.Item>Verification typically takes 2-3 business days.</List.Item>
                  </List>
                </Flex>
              </Flex>
            </Paper>

            <Flex justify="flex-end">
              <Button
                onClick={() => {
                  onClose();
                  openPaymentModal();
                }}
              >
                Proceed
              </Button>
            </Flex>
          </>
        )}
      </Modal>

      <PaymentMethodModal
        opened={paymentModalOpened}
        onClose={closePaymentModal}
        onConfirm={() => {
          closePaymentModal();
          onApply();
        }}
        loading={isLoading}
        creditsRequired={verificationFeeCredits}
        showNote={false}
      />
    </>
  );
}
